import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import ConfidenceBadge from '../components/ConfidenceBadge';

/**
 * Phase 4 — Reviewer inbox.
 *
 * Lists every run currently in the ``submitted`` review state and links
 * out to the run-detail page where the reviewer's approve / reject
 * controls live. Auth: any reviewer / admin role.
 */
export default function ReviewerInboxPage() {
  const { token, user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const allowed = ['superadmin', 'admin', 'reviewer'].includes(user?.role);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/review/inbox`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} — ${t.slice(0, 200)}`);
      }
      const data = await r.json();
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load reviewer queue.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  if (!allowed) {
    return (
      <div data-testid="reviewer-inbox-forbidden" style={{ padding: '2rem' }}>
        <h2>Reviewer access required</h2>
        <p style={{ color: 'var(--ux-text-muted)' }}>
          Your role <code>{user?.role || 'unknown'}</code> cannot view the
          reviewer queue. Ask an admin to grant you the <code>reviewer</code>
          role.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="reviewer-inbox-page" style={{ padding: '2rem', maxWidth: 1100 }}>
      <header style={{ marginBottom: '1.4rem' }}>
        <h1 style={{ margin: 0 }}>Reviewer queue</h1>
        <p style={{ color: 'var(--ux-text-muted)', marginTop: '0.4rem' }}>
          Successful runs awaiting reviewer sign-off. Click any run to open
          the detail view, where the approve / reject controls live.
        </p>
        <button
          type="button"
          data-testid="reviewer-inbox-refresh"
          onClick={refresh}
          disabled={loading}
          style={{
            marginTop: '0.5rem',
            fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem',
            padding: '5px 10px', borderRadius: 6,
            background: '#0f1419', color: '#eef3f8',
            border: '1px solid #2c3a48', cursor: 'pointer',
          }}
        >
          {loading ? '· Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && (
        <div data-testid="reviewer-inbox-error" style={{ color: '#b15a00', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {!loading && runs.length === 0 && !error && (
        <div data-testid="reviewer-inbox-empty" style={{ padding: '2rem', color: 'var(--ux-text-muted)' }}>
          No runs awaiting review. The queue is clean.
        </div>
      )}

      {runs.length > 0 && (
        <table
          data-testid="reviewer-inbox-table"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}
        >
          <thead>
            <tr style={{ background: 'var(--ux-bg-soft)', textAlign: 'left' }}>
              <th style={th}>Run</th>
              <th style={th}>Submitted</th>
              <th style={th}>Submitted by</th>
              <th style={th}>Confidence</th>
              <th style={th}>Success rate</th>
              <th style={th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const review = r.review || {};
              const conf = (r.provenance || {}).confidence;
              const sr = (r.aggregate || {}).success_rate;
              return (
                <tr key={r.id} data-testid={`reviewer-inbox-row-${r.id}`} style={{ borderBottom: '1px solid var(--ux-border)' }}>
                  <td style={td}><code>{r.id}</code></td>
                  <td style={td}>{review.submitted_at || '—'}</td>
                  <td style={td}>{review.submitted_by || '—'}</td>
                  <td style={td}><ConfidenceBadge confidence={conf} compact /></td>
                  <td style={td}>{typeof sr === 'number' ? sr.toFixed(2) : '—'}</td>
                  <td style={td}>
                    <Link
                      to={`/runs/${r.id}`}
                      data-testid={`reviewer-inbox-open-${r.id}`}
                      style={{ color: 'var(--ux-accent)', textDecoration: 'none' }}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const th = { padding: '8px 10px', fontWeight: 600, color: 'var(--ux-text-muted)' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
